/* eslint-disable @typescript-eslint/no-explicit-any */
import { Cmd, loop } from 'redux-loop';
import {
  Comm,
  CommMessage,
  Effects,
  ID,
  PartyUpdate,
  ServiceStateStrictWithEffects,
  StateUpdateWithoutParty,
  TMCoreAction,
  TableLayout,
  TextMessage,
} from 'state/service/appTs/types';
import {
  ElmPartyType,
  ElmPatron,
  ElmWalkInPartyDescription,
  PartyFromElm,
  TableFromElm,
  Topic,
} from 'types/service';
import { ServiceAppError } from 'state/service/appTs/serviceErrors';
import {
  ServiceStateAppFloorplan,
  ServiceStateAppStrict,
  ServiceStateHistory,
  ServiceStateStrict,
} from 'state/service/app';
import {
  addValueToMultimap,
  getKeysFromValueInMultimap,
  getValuesFromKeyInMultimap,
} from 'state/service/appTs/utils';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { decodeTextMessage } from 'state/service/appTs/decoders';
import { encodeOutboundAction } from 'state/service/appTs/encoders';
import { update as tmCoreUpdate } from 'state/service/appTs/tmCore';
import _ from 'lodash';

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L403-L413
 */
export function withTableAndParty(
  app: ServiceStateAppStrict,
  tableId: ID,
  partyId: ID,
  updateFn: (table: TableFromElm, party: PartyFromElm) => ServiceStateAppStrict
): ServiceStateAppStrict {
  const table = app.tables[tableId];
  if (!table) {
    throw new ServiceAppError('Not a valid table');
  }

  const party = app.parties[partyId];
  if (!party) {
    throw new ServiceAppError('Not a valid party');
  }

  return updateFn(table, party);
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L416-L427
 */
export function addTableToParty(app: ServiceStateAppStrict) {
  return (table: TableFromElm, party: PartyFromElm): ServiceStateAppStrict => {
    return {
      ...app,
      partyIdToTableId: addValueToMultimap(app.partyIdToTableId, party.id, table.id),
      serverIdToPartyIds: table.businessServerId
        ? addValueToMultimap(app.serverIdToPartyIds, table.businessServerId, party.id)
        : app.serverIdToPartyIds,
    };
  };
}

export function checkPartyDoesNotExist(partyId: ID, model: ServiceStateAppStrict): void {
  if (model.parties[partyId]) {
    throw new ServiceAppError(`Party ID ${partyId} already exists`);
  }
}

export function addPartyToPatron(
  patronId: ID,
  partyId: ID,
  patronIdToPartyIds: DictionaryStrict<number[]>
): DictionaryStrict<number[]> {
  return {
    ...patronIdToPartyIds,
    [patronId]: _.sortBy([...(patronIdToPartyIds[patronId] || []), partyId]),
  };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L359-L400
 */
export function walkInParty(description: ElmWalkInPartyDescription): PartyFromElm {
  const {
    id,
    arrivalTime,
    expectedReservationTime,
    numPeoplePresent,
    leftTime,
    quotedTimeMinutes,
    seatedTime,
    secondaryState,
    shortNotes,
    state,
    tag,
    textMessage,
    walkInCreatedTimeSec,
  } = description;
  return {
    id,
    arrivalTime,
    expectedPartySize: numPeoplePresent,
    expectedReservationTime:
      expectedReservationTime && expectedReservationTime !== '' ? expectedReservationTime : null,
    ticketDateTime:
      expectedReservationTime && expectedReservationTime !== ''
        ? expectedReservationTime
        : undefined,
    experience: !_.isNil(description.quotedTimeMinutes) ? 'Walk-in' : 'Expected Walk-in',
    experienceId: undefined,
    leftTime,
    numPeoplePresent,
    quotedTimeMinutes,
    seatedTime,
    secondaryState,
    shortNotes,
    state: state || 'EXPECTED',
    tag,
    textMessage: textMessage ? textMessage.map(decodeTextMessage) : [],
    walkInCreatedTimeSec: walkInCreatedTimeSec || arrivalTime,
    patronInfo: computePatron(description),
    partyType: { visitId: undefined, partyType: 'WalkInParty' },
    walkInPartyDescription: description,
  };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L826-L835
 */
export function recomputeServerPartyMapping(app: ServiceStateAppStrict): ServiceStateAppStrict {
  return {
    ...app,
    serverIdToPartyIds: _.reduce(
      serverIds(app.tables),
      (serverIdToPartyIds: ServiceStateAppStrict['serverIdToPartyIds'], serverId) => {
        const tableIds = tablesForServer(app.tables, serverId);
        const partyIds = _.flatMap(tableIds, (tableId) =>
          partiesOnTable(app.partyIdToTableId, tableId)
        );
        if (!_.isEmpty(partyIds)) {
          serverIdToPartyIds[serverId] = partyIds;
        }
        return serverIdToPartyIds;
      },
      {}
    ),
  };
}

/**
 * Returns an appropriate name to call the party in UI elements.
 *
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/Entities/Party.elm#L231-L241
 */
export function displayPartyName(party: PartyFromElm): string {
  return party.patronInfo.lastName || party.patronInfo.firstName || 'Unnamed';
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L1184-L1186
 */
export function replacePartyInState(
  app: ServiceStateAppStrict,
  newParty: PartyFromElm
): ServiceStateAppStrict {
  return replacePartyInAppState(app, newParty);
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L1365-L1377
 */
export function seatAtTableIfPossible(
  app: ServiceStateAppStrict,
  party: PartyFromElm,
  timestamp: number
): ServiceStateAppStrict {
  if (!party.seatedTime) {
    const seatingError = checkForSeatingError(app, party);
    if (seatingError) {
      throw new ServiceAppError(seatingError);
    }
    return seatAtTable(app, party, timestamp);
  } else {
    return replacePartyInAppState(app, { ...party, leftTime: undefined });
  }
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/Entities/Party.elm#L125-L156
 *
 * Updates a local party with information received from the server, preserving information that the client knows in more detail than the server.
 *
 * @param clientParty The old state of the party from the store
 * @param partyUpdate The new state of the party from the server
 */
export function updateWithServerState(
  clientParty: PartyFromElm,
  partyUpdate: PartyFromElm
): PartyFromElm {
  const clientExpectedReservationTime = clientParty.expectedReservationTime;
  const clientListedPurchaseTime = clientParty.ticketDateTime;
  // If the expected reservation time based on purchase has changed, use the one changed on the client. Else use the one we just received by the server.
  const expectedReservationTime =
    clientExpectedReservationTime && clientListedPurchaseTime
      ? clientExpectedReservationTime === clientListedPurchaseTime
        ? partyUpdate.expectedReservationTime
        : clientExpectedReservationTime
      : partyUpdate.expectedReservationTime;
  const partyWithInServiceEditsRemoved = updateWithEntitySetFromDb(clientParty, partyUpdate);
  const numPeoplePresent =
    clientParty.expectedPartySize === clientParty.numPeoplePresent
      ? partyUpdate.expectedPartySize
      : clientParty.numPeoplePresent;
  return {
    ...partyWithInServiceEditsRemoved,
    numPeoplePresent,
    expectedReservationTime,
  };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L1172-L1174
 */
export function applyPartyUpdate(
  model: ServiceStateAppStrict,
  partyUpdate: PartyUpdate
): ServiceStateAppStrict {
  const parties = model.parties;
  const party = parties[partyUpdate.partyId];
  if (!party) {
    return model;
  }
  return {
    ...model,
    parties: {
      ...parties,
      [partyUpdate.partyId]: applyPartyUpdateToParty(partyUpdate, party),
    },
  };
}

export function computePartyTypeFromId(id: number): ElmPartyType {
  if (id > 0) {
    return {
      partyType: 'PurchaseParty',
      originalPurchaseId: id,
    };
  } else {
    return {
      partyType: 'WalkInParty',
    };
  }
}

/**
 * Takes all appropriate actions to update the input party to match the party information in the
 * given model. This involves replacing the party's record within the model as well as possibly
 * updating other data structures in the model for consistency.
 *
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L1208-L1217
 */
export function handleTableUpdate(
  app: ServiceStateAppStrict,
  table: TableFromElm
): ServiceStateAppStrict {
  const originalTable = app.tables[table.id];
  if (!originalTable) {
    throw new ServiceAppError(`Table ${table.id} not found`);
  }
  if (tableHasSeatedParty(app, table.id) && table.state !== 'Ready') {
    throw new ServiceAppError(`Table ${table.name} has a seated party.`);
  }

  const updatedTable = {
    ...table,
    businessServerId: originalTable.businessServerId,
    tag: originalTable.tag,
    // state: originalTable.state, TODO: I removed this to make a test pass, but it might still be necessary in some cases
  };

  return {
    ...app,
    tables: { ...app.tables, [updatedTable.id]: updatedTable },
    floorplan: app.floorplan && updateFloorplan(app.floorplan, updatedTable),
  };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/Entities/Party.elm#L281-L310
 */
export function eligibleForAutoClose(party: PartyFromElm): boolean {
  switch (party.state) {
    case 'EXPECTED':
      return false;
    case 'NO_SHOW':
      return false;
    case 'CANCELED':
      return false;
    case 'ARRIVED':
      return true;
    case 'PARTIALLY_ARRIVED':
      return true;
    case 'PARTIALLY_SEATED':
      return true;
    case 'SEATED':
      return true;
    case 'LEFT':
      return false;
    // istanbul ignore next
    default:
      return assertNever(party.state);
  }
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/Entities/Floorplan.elm#L84-L91
 */
export function getTables(tableLayout: TableLayout): DictionaryStrict<TableFromElm> {
  switch (tableLayout.type) {
    case 'NoFloorplan':
      return tableLayout.tables;
    case 'HasFloorplan':
      return tableLayout.floorplan.tables;
    default:
      return assertNever(tableLayout);
  }
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/Entities/Floorplan.elm#L38-L41
 */
export function getTable(tableId: ID, tableLayout: TableLayout): TableFromElm | undefined {
  return getTables(tableLayout)[tableId];
}

export function getTableLayout(app: ServiceStateAppStrict): TableLayout {
  // TODO: actual implementation, which works with floorplans
  return {
    type: 'NoFloorplan',
    tables: app.tables,
  };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/Entities/Floorplan.elm#L64-L81
 */
export function addTableToFloorplan(
  app: ServiceStateAppStrict,
  table: TableFromElm
): ServiceStateAppStrict {
  return { ...app, tables: { ...app.tables, [table.id]: table } }; // TODO actual implementation
}

export function addTextMessageForPhone(
  party: PartyFromElm,
  textMessage: TextMessage
): PartyFromElm {
  return normalizePhone(party.patronInfo.phone) === normalizePhone(textMessage.patronTextNumber)
    ? { ...party, textMessage: _.sortBy([...party.textMessage, textMessage], 'id') }
    : party;
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L173-L200
 */
export function removePartyFromPatron(app: ServiceStateAppStrict, partyId: ID) {
  return app.patronIdToPartyIds; // TODO
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L1220-L1252
 */
export function updateStateOfPartyAtTable(
  app: ServiceStateAppStrict,
  table: TableFromElm,
  stateUpdateWithoutParty: StateUpdateWithoutParty
): ServiceStateAppStrict {
  return app; // TODO
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/History.elm#L66-L68
 */
export function markAllAsSeen(history: ServiceStateHistory): ServiceStateHistory {
  return { ...history, items: history.items.map((item) => ({ ...item, hasBeenSeen: true })) };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMClient.elm#L423-L425
 */
export function updateHistory(model: ServiceStateStrict): ServiceStateStrict {
  return { ...model, history: { ...model.history, isVisible: model.historyToggledOn } };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/Topic.elm#L27-L29
 */
export function sameEventStream(t1: Topic, t2: Topic): boolean {
  return t1.serviceDate === t2.serviceDate && t1.selectedBusiness.id === t2.selectedBusiness.id;
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMClient.elm#L428-L455
 */
export function updateModelWithLocalActions(
  model: ServiceStateStrict,
  timestampSecs: number,
  actions: TMCoreAction[]
): ServiceStateStrict {
  // TODO actual implementation
  let updatedModel = model;
  for (const action of actions) {
    updatedModel = { ...updatedModel, app: tmCoreUpdate(updatedModel.app, action) };
  }
  return updatedModel;
}

/**
 * Given a core model and a list of actions, builds a `CommMessage` that represents taking all of
 * those actions on the input model (in sequence).
 *
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMClient.elm#L370-L380
 */
export function buildCommMessageForActions(actions: TMCoreAction[]): CommMessage {
  if (actions.length === 1) {
    return { type: 'Core', action: actions[0] };
  }
  return { type: 'Batch', actions: actions.map((action) => ({ type: 'Core', action })) };
}

/**
 * Sends a message and configures the communicator to expect a response.
 *
 * The result of this function is an effect that sends the given message, which leads to the given
 * state in the local client.
 *
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/Communicator.elm#L104-L116
 */
export function communicatorSend(message: CommMessage, communicator: Comm): [Effects, Comm] {
  return sendNextMessageIfReady({
    ...communicator,
    messagesToSend: [...communicator.messagesToSend, message],
  });
}

function sendNextMessageIfReady(communicator: Comm): [Effects, Comm] {
  if (communicator.messagePendingResponse) {
    return [[], communicator];
  }

  if (communicator.messagesToSend.length === 0) {
    return [[], communicator];
  }

  const [message, ...restOfQueue] = communicator.messagesToSend;
  const effect = {
    action: message,
    serverId: communicator.latestServerId,
    localId: communicator.nextInternalId,
  };

  return [
    [effect],
    {
      ...communicator,
      messagePendingResponse: { message, id: communicator.nextInternalId },
      messagesToSend: restOfQueue,
      nextInternalId: communicator.nextInternalId + 1,
    },
  ];
}

export function updateModelWithSentChat(
  timestampSecs: number,
  chatMessage: any, // TODO
  updatedModel: ServiceStateStrict
) {
  // TODO
  return null as any;
}

export function modelWithEffects(
  model: ServiceStateStrict,
  effects: Effects
): ServiceStateStrictWithEffects {
  return loop(
    model,
    Cmd.list(
      effects.map(encodeOutboundAction).map((outboundAction) => {
        if (window.elmAppTs) {
          return Cmd.run(window.elmAppTs.outboundWebsocketMessageFn, {
            args: [outboundAction],
          });
        }
        return Cmd.none;
      })
    )
  );
}

/**
 * A convenience method to return just the model in an action that sometimes
 * returns effects and sometimes does not.
 */
export function modelWithNoEffects(model: ServiceStateStrict): ServiceStateStrictWithEffects {
  return loop(model, Cmd.none);
}

function updateFloorplan(
  floorplan: ServiceStateAppFloorplan,
  updatedTable: TableFromElm
): ServiceStateAppFloorplan {
  const tables = [...floorplan.activeTable, ...floorplan.archivedTable].map((t) =>
    t.id === updatedTable.id ? updatedTable : t
  );
  return {
    ...floorplan,
    activeTable: tables.filter((t) => !t.isArchived),
    archivedTable: tables.filter((t) => t.isArchived),
  };
}

function serverIds(tables: ServiceStateAppStrict['tables']): ID[] {
  return _.uniq(_.compact(_.map(tables, (t) => t && t.businessServerId)));
}

function partiesOnTable(
  partyIdToTableId: ServiceStateAppStrict['partyIdToTableId'],
  tableId: ID
): ID[] {
  return Object.keys(
    _.pickBy(partyIdToTableId, (tableIds) => tableIds && tableIds.includes(tableId))
  ).map(Number);
}

function tablesForServer(tables: ServiceStateAppStrict['tables'], serverId: ID): ID[] {
  return _.compact(_.map(tables, (t) => t && t.businessServerId === serverId && t.id));
}

function replacePartyInAppState(
  app: ServiceStateAppStrict,
  newParty: PartyFromElm
): ServiceStateAppStrict {
  return updatePartyInStateById(app, newParty.id, () => newParty);
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L1189-L1193
 */
function updatePartyInStateById(
  app: ServiceStateAppStrict,
  partyId: ID,
  updateFn: (party: PartyFromElm) => PartyFromElm
): ServiceStateAppStrict {
  const oldParty = app.parties[partyId];
  return {
    ...app,
    parties: { ...app.parties, [partyId]: oldParty && updateFn(oldParty) },
  };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L1380-L1416
 */
function checkForSeatingError(app: ServiceStateAppStrict, party: PartyFromElm): string | undefined {
  const tableIdsForParty = getValuesFromKeyInMultimap(app.partyIdToTableId, party.id);
  const tablesForParty = _.compact(tableIdsForParty.map((tableId) => app.tables[tableId]));
  const notReadyTables = tablesForParty.filter((table) => table.state !== 'Ready');
  const seatedTableIds = tableIdsForParty.filter((tableId) => tableHasSeatedParty(app, tableId));
  const seatedTables = _.compact(seatedTableIds.map((tableId) => app.tables[tableId]));
  if (_.isEmpty(tableIdsForParty)) {
    return `The ${displayPartyName(party)} party has no table so cannot be seated`;
  } else if (!_.isEmpty(seatedTableIds)) {
    return `Table ${seatedTables.map((table) => table.name).join(', ')} already has a party seated`;
  } else if (!_.isEmpty(notReadyTables)) {
    return `Table ${notReadyTables.map((table) => table.name).join(', ')} is not marked as ready`;
  }
  return;
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L1429-L1436
 */
function tableHasSeatedParty(app: ServiceStateAppStrict, tableId: ID): boolean {
  return !!getSeatedPartyAtTable(app, tableId);
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L1439-L1448
 */
function getSeatedPartyAtTable(app: ServiceStateAppStrict, tableId: ID): PartyFromElm | undefined {
  const partyIdsAtTable = getKeysFromValueInMultimap(app.partyIdToTableId, tableId);
  const parties = _.compact(partyIdsAtTable.map((partyId) => app.parties[Number(partyId)]));
  const seatedParties = parties.filter((party) => isSeated(party));
  return seatedParties[0]; // will return undefined if there are no seated parties at table
}

/**
 * Determines if the given party is currently seated.
 *
 * https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/Entities/Party.elm#L246-L248
 */
function isSeated(party: PartyFromElm): boolean {
  return ['SEATED', 'PARTIALLY_SEATED'].includes(party.state);
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L1419-L1426
 */
function seatAtTable(
  app: ServiceStateAppStrict,
  party: PartyFromElm,
  timestamp: number
): ServiceStateAppStrict {
  return replacePartyInAppState(app, {
    ...party,
    arrivalTime: party.arrivalTime || timestamp,
    seatedTime: timestamp,
    leftTime: undefined,
  });
}

/**
 * Updates the party with what's in the DB. The keys we keep from the client are only those that are
 * not set in PurchaseEntity.java `convertToParty`
 *
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/Entities/Party.elm#L162-L222
 */
function updateWithEntitySetFromDb(
  currentParty: PartyFromElm,
  partyFromDb: PartyFromElm
): PartyFromElm {
  const {
    arrivalTime,
    leftTime,
    quotedTimeMinutes,
    seatedTime,
    secondaryState,
    shortNotes,
    state,
    tag,
    textMessage,
    walkInCreatedTimeSec,
  } = currentParty;

  let expectedReservationTime = currentParty.expectedReservationTime;
  let numPeoplePresent = currentParty.numPeoplePresent;

  const currentPartyType = currentParty.partyType;
  const dbPartyType = partyFromDb.partyType;
  if (currentPartyType.partyType === 'PurchaseParty' && dbPartyType.partyType === 'PurchaseParty') {
    if (currentPartyType.currentPurchaseId === dbPartyType.currentPurchaseId) {
      expectedReservationTime = currentParty.expectedReservationTime;
      numPeoplePresent = currentParty.numPeoplePresent;
    } else {
      expectedReservationTime = partyFromDb.expectedReservationTime;
      numPeoplePresent = partyFromDb.numPeoplePresent;
    }
  } else if (
    currentPartyType.partyType === 'WalkInParty' &&
    dbPartyType.partyType === 'WalkInParty'
  ) {
    if (currentPartyType.visitId === dbPartyType.visitId) {
      expectedReservationTime = currentParty.expectedReservationTime;
      numPeoplePresent = currentParty.numPeoplePresent;
    } else {
      expectedReservationTime = partyFromDb.expectedReservationTime;
      numPeoplePresent = partyFromDb.numPeoplePresent;
    }
  }

  return {
    ...partyFromDb,
    arrivalTime,
    expectedReservationTime,
    leftTime,
    numPeoplePresent,
    patronInfo:
      currentParty.patronInfo.id === partyFromDb.patronInfo.id
        ? currentParty.patronInfo
        : partyFromDb.patronInfo,
    quotedTimeMinutes,
    seatedTime,
    secondaryState,
    shortNotes,
    state: partyFromDb.state === 'CANCELED' ? 'CANCELED' : state,
    tag,
    textMessage,
    walkInCreatedTimeSec,
  };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L1090-L1156
 */
function applyPartyUpdateToParty(partyUpdate: PartyUpdate, party: PartyFromElm): PartyFromElm {
  switch (partyUpdate.updateType) {
    case 'AddTag': {
      return {
        ...party,
        tag: [partyUpdate.newValue, ...party.tag],
      };
    }
    case 'RemoveTag': {
      return {
        ...party,
        tag: _.filter(party.tag, (t) => t !== partyUpdate.newValue),
      };
    }
    case 'SetShortNotes': {
      return {
        ...party,
        shortNotes: partyUpdate.newValue,
      };
    }
    case 'UpdatePartyFirstName': {
      return {
        ...party,
        patronInfo: {
          ...party.patronInfo,
          firstName: partyUpdate.newValue,
        },
      };
    }
    case 'UpdatePartyLastName': {
      return {
        ...party,
        patronInfo: {
          ...party.patronInfo,
          lastName: partyUpdate.newValue,
        },
      };
    }
    case 'UpdatePartyPhone': {
      return {
        ...party,
        patronInfo: {
          ...party.patronInfo,
          phone: partyUpdate.newValue === '' ? undefined : partyUpdate.newValue,
        },
      };
    }
    case 'UpdatePartyPhoneCountryCode': {
      return {
        ...party,
        patronInfo: {
          ...party.patronInfo,
          phoneCountryCode: partyUpdate.newValue === '' ? undefined : partyUpdate.newValue,
        },
      };
    }
    case 'UpdatePartyQuotedTimeMinutes': {
      const parsedTime = _.parseInt(partyUpdate.newValue);
      return {
        ...party,
        quotedTimeMinutes: _.isNaN(parsedTime) ? undefined : parsedTime,
      };
    }
    case 'UpdatePartySize': {
      const parsedSize = _.parseInt(partyUpdate.newValue);
      return {
        ...party,
        numPeoplePresent: _.isNaN(parsedSize) ? party.numPeoplePresent : parsedSize,
      };
    }
    case 'UpdatePartyReservationTime': {
      // TODO(jacob, 2019-09-26): It looks like we were publishing the same
      // value in two slightly different ways in the elm code (through the Party
      // encoder). For now we preserve that behavior, but really we should get
      // rid of one of the two fields.
      return {
        ...party,
        expectedReservationTime: partyUpdate.newValue === '' ? null : partyUpdate.newValue,
        ticketDateTime: partyUpdate.newValue === '' ? undefined : partyUpdate.newValue,
      };
    }
    default:
      // istanbul ignore next
      assertNever(partyUpdate.updateType);
      return party;
  }
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L339-L356
 */
function computePatron(description: ElmWalkInPartyDescription): ElmPatron {
  const { email, patronId, firstName, lastName, phone, phoneCountryCode } = description;
  return {
    id: patronId,
    email,
    firstName,
    lastName,
    phone,
    phoneCountryCode,
  };
}

function normalizePhone(phone?: string): string | undefined {
  if (!phone) {
    return undefined;
  }
  const sanitizedNumber = phone.replace(/[^+\\d]/, '');
  if (sanitizedNumber.length < 2) {
    return undefined;
  } else if (!sanitizedNumber.startsWith('+')) {
    if (sanitizedNumber.length === 10) {
      return `+1${sanitizedNumber}`;
    } else {
      return `+${sanitizedNumber}`;
    }
  }
  return phone;
}
